import {clone} from 'lodash';
import React, {CSSProperties, FunctionComponent, useState} from 'react';
import Dropzone from 'react-dropzone';
import ReactMde from 'react-mde';
import 'react-mde/lib/styles/css/react-mde-all.css';
import {useDispatch} from 'react-redux';
import * as Showdown from 'showdown';
import swal from 'sweetalert2';
import {NewsPostEditorContentProps} from '../..';
import {getEndpoint, getHeaders} from '../../../../helpers/endpoint';
import {SocialNetworkId} from '../../../../types/newsFeed';
import {InputLabelEditor} from '../../../InputLabelEditor';
import {postImageUpload} from '../../../NotificationEditor/components/AvatarSelect';
import {appRoutesToRedirect} from '../../../NotificationEditor/components/NotificationEditorContent/constant';
import {Separator} from '../../../NotificationEditor/components/Separator';

const converter = new Showdown.Converter({
  tables: true,
  simplifiedAutoLink: true,
  strikethrough: true,
  tasklists: true,
});

const Styles: Record<string, CSSProperties> = {
  container: {
    position: 'relative',
    overflow: 'auto',
    height: 'calc(100vh - 300px)',
  },
  inputContainer: {
    marginBottom: 16,
  },
  flag: {
    margin: 8,
    height: 22,
    width: 22,
    borderRadius: 22,
    cursor: 'pointer',
  },
  socialContainer: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: 16,
    width: '100%',
  },
  selectedFlag: {
    width: 32,
    height: 32,
  },
  flagsContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  avatarContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  image: {
    width: 40,
    height: 40,
    marginRight: 16,
  },
  dropZone: {
    width: 360,
    minHeight: 200,
    border: '1px dashed #4054CC',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#4054CC',
    cursor: 'pointer',
  },
  label: {
    height: 32,
    border: '1px dashed #4054CC',
    borderRadius: 16,
    color: '#4054CC',
    paddingLeft: 8,
    paddingRight: 8,
    display: 'flex',
    alignItems: 'center',
    marginRight: 8,
  },
  labelContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 8,
  },
  addCtaButton: {
    marginTop: 16,
    marginBottom: 16,
    height: 40,
    border: '1px dashed #4054CC',
    borderRadius: 4,
    color: '#4054CC',
    backgroundColor: 'white',
  },
};

export const languagesFlagsRefs = {
  'fr-FR': 'fr',
  'en-GB': 'gb-eng',
  'es-ES': 'es',
};

export const LanguageSelector = ({
  onChange,
  selectedLanguage,
}: {
  onChange: (language: string) => void;
  selectedLanguage: string;
}) => {
  return (
    <div style={Styles.flagsContainer}>
      {Object.keys(languagesFlagsRefs).map(language => (
        <div onClick={() => onChange(language)}>
          <span
            style={{
              ...Styles.flag,
              ...(selectedLanguage === language && Styles.selectedFlag),
            }}
            key={language}
            className={`flag-icon flag-icon-${languagesFlagsRefs[language]} flag-icon-squared`}
          />
          {selectedLanguage === language && (
            <div
              style={{
                width: 40,
                backgroundColor: '#4054CC',
                height: 2,
                marginLeft: 4,
              }}
            />
          )}
        </div>
      ))}
    </div>
  );
};

export const getFacebookPostHtml = (
  url: string,
  onSuccess: (response: JSX.Element) => void,
) => {
  fetch(
    `https://graph.facebook.com/v9.0/oembed_post?url=${url}&access_token=1992776267665702%7CpeopBDRIqrBb44LRKhyiOgUQ7LM`,
    {
      method: 'GET',
    },
  )
    .then(response => response.json())
    .then(data => {
      if (data.error) {
        swal({
          title: `Oops...`,
          type: 'error',
          html: `Error generating embed, please verify the link: <b>${url}</b>`,
        });
      } else {
        onSuccess(data.html);
      }
    })
    .catch(error => {});
};

export const getFacebookVideoHtml = (
  url: string,
  onSuccess: (response: JSX.Element) => void,
) => {
  fetch(
    `https://graph.facebook.com/v9.0/oembed_video?url=${url}&access_token=1992776267665702%7CpeopBDRIqrBb44LRKhyiOgUQ7LM`,
    {
      method: 'GET',
    },
  )
    .then(response => response.json())
    .then(data => {
      if (data.error) {
        swal({
          title: `Oops...`,
          type: 'error',
          html: `Error generating embed, please verify the link: <b>${url}</b>`,
        });
      } else {
        onSuccess(data.html);
      }
    })
    .catch(error => {});
};

export const getInstagramHtml = (
  url: string,
  onSuccess: (response: JSX.Element) => void,
) => {
  fetch(
    `https://graph.facebook.com/v9.0/instagram_oembed?url=${url}&access_token=1992776267665702%7CpeopBDRIqrBb44LRKhyiOgUQ7LM`,
    {
      method: 'GET',
    },
  )
    .then(response => response.json())
    .then(data => {
      if (data.error) {
        swal({
          title: `Oops...`,
          type: 'error',
          html: `Error generating embed, please verify the link: <b>${url}</b>`,
        });
      } else {
        onSuccess(data.html);
      }
    })
    .catch(error => {});
};

export const getTwitterHtml = (
  url: string,
  onSuccess: (response: JSX.Element) => void,
) => {
  return (_dispatch, getState) => {
    return fetch(getEndpoint('getTwitterHtml'), {
      method: 'POST',
      headers: getHeaders(getState().authentication),
      body: JSON.stringify({
        url: `https://publish.twitter.com/oembed?url=${url}`,
      }),
    })
      .then(response => response.json())
      .then(data => {
        if (data.error) {
          swal({
            title: `Oops...`,
            type: 'error',
            html: `Error generating embed, please verify the link: <b>${url}</b>`,
          });
        } else {
          onSuccess(data.html);
        }
      })
      .catch(error => {});
  };
};

const socialNetworks: Record<
  SocialNetworkId,
  {
    icon: JSX.Element;
    generateHtml?: (
      url: string,
      onSuccess: (response: JSX.Element) => void,
    ) => void;
  }
> = {
  twitter: {
    icon: (
      <i
        style={{color: '#00ACEE', fontSize: 32}}
        className="fa fa-twitter-square"
      />
    ),
    generateHtml: getTwitterHtml,
  },
  facebookPost: {
    icon: (
      <i
        style={{color: '#3B5998', fontSize: 32}}
        className="fa fa-facebook-square"
      />
    ),
    generateHtml: getFacebookPostHtml,
  },
  facebookVideo: {
    icon: (
      <>
        <i
          style={{color: '#3B5998', fontSize: 28}}
          className="fa fa-facebook"
        />
        <i style={{color: '#3B5998'}} className="fa fa-play-circle" />
      </>
    ),
    generateHtml: getFacebookVideoHtml,
  },
  youtube: {
    icon: (
      <i
        style={{color: '#C4302B', fontSize: 32}}
        className="fa fa-youtube-square"
      />
    ),
  },
  instagram: {
    icon: (
      <i
        style={{
          fontSize: 32,
        }}
        className="fa fa-instagram"
      />
    ),
    generateHtml: getInstagramHtml,
  },
};

export const NewsPostEditorContent: FunctionComponent<NewsPostEditorContentProps> = ({
  data,
  onChange,
  isCreation,
  setErrorForSubmit,
}) => {
  const dispatch = useDispatch();
  const [selectedLanguage, setSelectedLanguage] = useState('fr-FR');
  const [selectedTab, setSelectedTab] = React.useState<'write' | 'preview'>(
    'write',
  );
  const dataByLanguage = data.localization?.[selectedLanguage];
  const [buttonsLinkError, setButtonsLinkError] = useState(
    dataByLanguage?.buttons?.map(() => false),
  );

  const checkForButtonLinkError = (
    buttonLink: string | undefined,
    index: number,
  ) => {
    let newButtonsError = clone(buttonsLinkError) || [];
    if (buttonLink?.indexOf('http') === -1 && buttonLink?.indexOf(':') !== -1) {
      newButtonsError[index] = true;
      // eslint-disable-next-line no-unused-expressions
      setErrorForSubmit?.(true);
      setButtonsLinkError(newButtonsError);
    } else {
      newButtonsError[index] = false;
      setButtonsLinkError(newButtonsError);
      // eslint-disable-next-line no-unused-expressions
      setErrorForSubmit?.(false);
    }
  };

  return (
    <div style={Styles.container}>
      <LanguageSelector
        onChange={language => setSelectedLanguage(language)}
        selectedLanguage={selectedLanguage}
      />
      <div style={Styles.inputContainer}>
        <InputLabelEditor
          title="TITLE"
          isRequired={!isCreation}
          flag={selectedLanguage}
        />
        <textarea
          className="form-control"
          value={dataByLanguage?.title || ''}
          onChange={e =>
            onChange(`localization.${selectedLanguage}.title`, e.target.value)
          }
        />
      </div>
      <Separator />
      <div style={Styles.inputContainer}>
        <InputLabelEditor
          title="CONTENT"
          isRequired={!isCreation}
          flag={selectedLanguage}
        />
        <ReactMde
          toolbarCommands={[
            ['bold', 'italic', 'strikethrough'],
            ['unordered-list', 'ordered-list'],
            ['link'],
          ]}
          value={dataByLanguage?.content || ''}
          onChange={value =>
            onChange(`localization.${selectedLanguage}.content`, value)
          }
          selectedTab={selectedTab}
          onTabChange={setSelectedTab}
          generateMarkdownPreview={markdown =>
            Promise.resolve(converter.makeHtml(markdown))
          }
        />
      </div>
      <Separator />
      <div style={Styles.inputContainer}>
        <InputLabelEditor title="IMAGE" flag={selectedLanguage} />
        <div style={{display: 'flex', flexDirection: 'row'}}>
          <Dropzone
            style={Styles.dropZone}
            onDrop={acceptedFiles => {
              const onSuccess = (imageUrl: string) => {
                onChange(`localization.${selectedLanguage}.imageUrl`, imageUrl);
              };
              dispatch(postImageUpload(acceptedFiles[0], onSuccess));
            }}>
            <div>
              {!dataByLanguage?.imageUrl ? (
                <span>Parcourir ou Drag n Drop</span>
              ) : (
                <img
                  src={dataByLanguage.imageUrl}
                  style={{maxWidth: 360}}
                  alt=""
                />
              )}
            </div>
          </Dropzone>
          {dataByLanguage?.imageUrl && (
            <div
              onClick={() =>
                onChange(`localization.${selectedLanguage}.imageUrl`, undefined)
              }
              style={{
                marginLeft: 16,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: 30,
                height: 30,
                backgroundColor: 'red',
                borderRadius: 30,
                cursor: 'pointer',
              }}>
              <i style={{color: 'white'}} className="fa fa-trash" />
            </div>
          )}
        </div>
      </div>
      <Separator />
      <div style={Styles.inputContainer}>
        <InputLabelEditor
          title="CTA"
          flag={selectedLanguage}
          isRequired={!isCreation}
        />
        {dataByLanguage?.buttons?.map((button, index) => (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              marginBottom: 16,
            }}>
            <div style={{flex: 1}}>
              <input
                placeholder="Title"
                style={{marginBottom: 8}}
                className="form-control"
                value={button.title || ''}
                onChange={e =>
                  onChange(
                    `localization.${selectedLanguage}.buttons[${index}].title`,
                    e.target.value,
                  )
                }
              />
              <div style={{display: 'flex', flexDirection: 'row'}}>
                <div style={{flex: 1, marginRight: 8}}>
                  <select
                    value={button.link || ''}
                    className="form-control"
                    onChange={e => {
                      checkForButtonLinkError(e.target.value, index);
                      onChange(
                        `localization.${selectedLanguage}.buttons[${index}].link`,
                        e.target.value,
                      );
                    }}>
                    <option value="">Select screen</option>
                    {Object.values(appRoutesToRedirect).map(route => (
                      <option key={route.screenName} value={route.path}>
                        {route.screenName}
                      </option>
                    ))}
                  </select>
                </div>
                <div
                  style={{flex: 1, marginLeft: 8}}
                  className={`form-group ${
                    buttonsLinkError?.[index] && 'has-error'
                  }`}>
                  <input
                    onBlur={() =>
                      checkForButtonLinkError(
                        dataByLanguage?.buttons?.[index].link,
                        index,
                      )
                    }
                    placeholder="https:// or inApp link"
                    className="form-control"
                    value={button.link}
                    onChange={e =>
                      onChange(
                        `localization.${selectedLanguage}.buttons[${index}].link`,
                        e.target.value,
                      )
                    }
                  />
                  {buttonsLinkError?.[index] && button?.link && (
                    <p
                      style={{
                        color: 'red',
                      }}>
                      You need to specify
                      <b> {button.link?.split(':')[1]?.split('/')[0]} </b>
                      in CTA redirection
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div>
              <div
                onClick={() => {
                  const newButtonsLinkError = clone(buttonsLinkError) || [];
                  const buttons = clone(dataByLanguage?.buttons) || [];
                  buttons.splice(index, 1);
                  newButtonsLinkError.splice(index, 1);
                  setButtonsLinkError(newButtonsLinkError);
                  onChange(`localization.${selectedLanguage}.buttons`, buttons);
                }}
                style={{
                  marginLeft: 16,
                  marginRight: 16,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: 30,
                  height: 30,
                  backgroundColor: 'red',
                  borderRadius: 30,
                  cursor: 'pointer',
                }}>
                <i style={{color: 'white'}} className="fa fa-trash" />
              </div>
            </div>
          </div>
        ))}
        <button
          onClick={() => {
            const buttons = clone(dataByLanguage?.buttons) || [];
            buttons.push({title: '', link: ''});
            const newsButtonsLinkError = clone(buttonsLinkError) || [];
            newsButtonsLinkError.push(false);
            setButtonsLinkError(newsButtonsLinkError);
            onChange(`localization.${selectedLanguage}.buttons`, buttons);
          }}
          style={Styles.addCtaButton}>
          <i style={{marginRight: 8}} className="fa fa-plus" />
          Ajouter un CTA
        </button>
        <Separator />
        <div style={Styles.inputContainer}>
          <InputLabelEditor
            title="RÉSEAUX SOCIAUX"
            isRequired={!isCreation}
            flag={selectedLanguage}
          />
          {Object.keys(socialNetworks).map(socialId => (
            <div
              style={{
                ...Styles.socialContainer,
                opacity:
                  !dataByLanguage?.socialEmbed ||
                  dataByLanguage?.socialEmbed?.[socialId]?.url
                    ? 1
                    : 0.5,
              }}>
              <div style={{marginRight: 16}}>
                {socialNetworks[socialId].icon}
              </div>
              <div style={{flex: 1}}>
                <input
                  disabled={
                    dataByLanguage?.socialEmbed &&
                    !dataByLanguage?.socialEmbed?.[socialId]
                  }
                  onBlur={() => {
                    if (dataByLanguage?.socialEmbed?.[socialId]?.url) {
                      const onSuccess = (html: JSX.Element) => {
                        dataByLanguage?.socialEmbed?.[socialId]?.url &&
                          onChange(
                            `localization.${selectedLanguage}.socialEmbed.${socialId}.html`,
                            html,
                          );
                      };
                      if (socialId === 'twitter') {
                        dispatch(
                          socialNetworks[socialId].generateHtml?.(
                            dataByLanguage?.socialEmbed?.[socialId]?.url,
                            onSuccess,
                          ),
                        );
                      } else {
                        // eslint-disable-next-line no-unused-expressions
                        socialNetworks[socialId].generateHtml?.(
                          dataByLanguage?.socialEmbed?.[socialId]?.url,
                          onSuccess,
                        );
                      }
                    }
                  }}
                  placeholder="https://"
                  className="form-control"
                  value={dataByLanguage?.socialEmbed?.[socialId]?.url || ''}
                  onChange={e => {
                    if (!e.target.value) {
                      onChange(
                        `localization.${selectedLanguage}.socialEmbed`,
                        undefined,
                      );
                    } else {
                      onChange(
                        `localization.${selectedLanguage}.socialEmbed.${socialId}.url`,
                        e.target.value,
                      );
                    }
                  }}
                />
                {socialId === 'youtube' &&
                  dataByLanguage?.socialEmbed?.[socialId]?.url?.indexOf(
                    'embed',
                  ) === -1 && (
                    <p style={{color: 'red'}}>Url needs to be an embed link</p>
                  )}
              </div>
              {dataByLanguage?.socialEmbed?.[socialId]?.url && (
                <div
                  onClick={() =>
                    onChange(
                      `localization.${selectedLanguage}.socialEmbed`,
                      undefined,
                    )
                  }
                  style={{
                    marginLeft: 16,
                    marginRight: 16,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: 30,
                    height: 30,
                    backgroundColor: 'red',
                    borderRadius: 30,
                    cursor: 'pointer',
                  }}>
                  <i style={{color: 'white'}} className="fa fa-trash" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/components/NewsPostEditor/components/NewsPostEditorContent/index.tsx